import React, { useEffect, useState } from 'react';
import { Plus, ShoppingBag, Link2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import SolicitarCompraAsistidaModal from './SolicitarCompraAsistidaModal';
import PagoPayphoneCompraAsistida from './PagoPayphoneCompraAsistida';

const ESTADO_LABEL = {
  nueva: 'Recibida',
  cotizando: 'Cotizando',
  cotizacion_enviada: 'Cotización enviada — revisa tu correo/WhatsApp',
  confirmada: 'Confirmada',
  rechazada: 'Rechazada',
  sin_respuesta: 'Sin respuesta',
};

const ESTADO_COLORS = {
  nueva: { bg: '#E3F2FD', color: '#1565C0' },
  cotizando: { bg: '#FFF3E0', color: '#E65100' },
  cotizacion_enviada: { bg: '#FFF9C4', color: '#F57F17' },
  confirmada: { bg: '#E8F5E9', color: '#2E7D32' },
  rechazada: { bg: '#FFEBEE', color: '#C62828' },
  sin_respuesta: { bg: '#FAFAFA', color: '#9E9E9E' },
};

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function CompraAsistidaCliente() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);

  const cargar = () => {
    setCargando(true);
    api.get('/compra-asistida/', { params: { ordering: '-creado' } })
      .then((res) => setSolicitudes(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar tus solicitudes.'))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
        <div>
          <h2 style={{ marginBottom: '2px' }}>Compra Asistida</h2>
          <p className="text-secondary text-sm" style={{ marginBottom: 0 }}>
            ¿No tienes tarjeta internacional o prefieres que compremos por ti? Solicítalo aquí.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setMostrarModal(true)}>
          <Plus size={16} /> Nueva solicitud
        </button>
      </div>

      {cargando ? (
        <p className="text-secondary">Cargando...</p>
      ) : solicitudes.length === 0 ? (
        <div className="empty-state">
          <ShoppingBag size={32} style={{ opacity: 0.4, marginBottom: '8px' }} />
          <p>Todavía no tienes solicitudes de compra asistida.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 'var(--spacing-4)' }}>
          {solicitudes.map((s) => {
            const ec = ESTADO_COLORS[s.estado] || ESTADO_COLORS.nueva;
            const mostrandoPago = s.total_pagar_ahora && !s.pagado && s.estado === 'cotizacion_enviada';
            return (
              <div key={s.id} className="card" style={{
                padding: 'var(--spacing-4)', minWidth: 0,
                gridColumn: mostrandoPago ? '1 / -1' : undefined,
                maxWidth: mostrandoPago ? '480px' : undefined,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-2)' }}>
                  <span className="text-sm text-secondary">{formatFecha(s.creado)}</span>
                  <span style={{
                    background: ec.bg, color: ec.color, borderRadius: '6px',
                    padding: '3px 8px', fontWeight: 600, fontSize: '11px',
                  }}>
                    {ESTADO_LABEL[s.estado] || s.estado}
                  </span>
                </div>
                <div style={{ display: 'grid', gap: '6px' }}>
                  {(s.items || []).map((it) => (
                    <div key={it.id} className="text-sm">
                      <strong>{it.descripcion}</strong>
                      {it.cantidad > 1 && <span className="text-secondary"> (x{it.cantidad})</span>}
                      {it.link && (
                        <a href={it.link} target="_blank" rel="noopener noreferrer"
                          style={{ marginLeft: '6px', color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                          <Link2 size={11} /> <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
                {s.total_pagar_ahora ? (
                  <div className="card" style={{ background: 'var(--color-bg-secondary)', padding: 'var(--spacing-2) var(--spacing-3)', marginTop: 'var(--spacing-3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 700 }}>
                      <span>A pagar ahora</span>
                      <span className="mono">${Number(s.total_pagar_ahora).toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-secondary" style={{ margin: '4px 0 0' }}>
                      + ${Number(s.envio_estimado).toFixed(2)} estimado de envío/aduana al llegar el paquete
                    </p>
                    {s.pagado ? (
                      <p className="text-xs" style={{ margin: '8px 0 0', color: '#2E7D32', fontWeight: 600 }}>
                        ✓ Pagado{s.payphone_tarjeta ? ` — ${s.payphone_tarjeta}` : ''}
                      </p>
                    ) : s.estado === 'cotizacion_enviada' && (
                      <PagoPayphoneCompraAsistida solicitud={s} />
                    )}
                  </div>
                ) : s.presupuesto_estimado && (
                  <p className="text-sm text-secondary" style={{ marginTop: 'var(--spacing-2)', marginBottom: 0 }}>
                    Presupuesto: ${Number(s.presupuesto_estimado).toFixed(2)}
                  </p>
                )}
                {(s.paquetes_generados || []).length > 0 && (
                  <div style={{ marginTop: 'var(--spacing-3)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-2)' }}>
                    <p className="text-xs text-secondary" style={{ marginBottom: '4px' }}>Ya la compramos, en camino:</p>
                    {s.paquetes_generados.map((p) => (
                      <p key={p.id} className="text-sm" style={{ marginBottom: '2px' }}>
                        <span className="mono">{p.tracking}</span>
                        {' — '}
                        {p.estado_actual_detalle?.nombre || '—'}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mostrarModal && (
        <SolicitarCompraAsistidaModal
          onClose={() => setMostrarModal(false)}
          onCreada={() => { setMostrarModal(false); cargar(); }}
        />
      )}
    </div>
  );
}
